import { IPNLDetailsEntity } from "@domains/entities/interfaces/Reports/PNLReport/iPNLDetails";
import { IPNLSummaryEntity } from "@domains/entities/interfaces/Reports/PNLReport/iPNLSummary";
import { DropDownEntity } from "@/components/Reports/CommonComponent/constant";
import { TransactionType } from "./iPNLTransactionReport";
import { FilterData, SortData } from "./iReportsCommon";
/**
 * @interface
 * @param {IPNLSummaryEntity} pnLSummary - The summary for scrips of profit and loss.
 * @param {IPNLDetailsEntity[]} pnLDetails - Scrips of profit and loss.
 */
export interface IPNLReportEntity {
  pnLSummary: IPNLSummaryEntity;
  pnLDetails: IPNLDetailsEntity[];
  pnlDataFlags: IPnlDataFlags;
}
export interface IPnlDataFlags {
  isBodEnabled?: boolean;
  bodProcessInfoMessage?: string;
}
export interface IPNLMainRequestData {
  fromDate: string;
  toDate: string;
  assetType?: AssetType | AssetType[];
  abortSignal?: AbortSignal;
  accountIds?: string;
  duration?: string;
}

export interface IPNLReportFilterState<T> {
  key: keyof T | Array<string>;
  value: any;
}
export interface IPNLSelectedTradeDetails {
  tradeDetails: IPNLDetailsEntity;
  assetType: AssetType | AssetType[];
  transactionType: TransactionType;
  fromDate: string;
  toDate: string;
}
export interface IPNLSelectedDateDetails {
  assetType: AssetType;
  fromDate: Date;
  toDate: Date;
}
export interface IPNLReportMainEntityContainer {
  PnLData: IPNLReportEntity;
  SearchData: string;
  FilterData: FilterData;
  SortData: Array<SortData>;
  IsSummarize: boolean;
}
export enum AssetType {
  EQ = "EQ",
  UnlistedEquity = "UEQ",
  FixedIncome = "FI",
  CertOfDeposit = "COD",
  CommPaper = "CP",
  NCD = "NCD",
  Warrant = "W",
  Bonds = "BO",
  Debentures = "DB",
  IndianDepoReceipt = "IDR",
  PreferenceShare = "PS",
  CorpFD = "CFD",
  RBIBonds = "RBO",
  EC54 = "54EC",
  Derivatives = "FnO",
  DEquity = "DEQ",
  DCurrency = "DCU",
  DCommodity = "DCO",
  MP = "MAP",
  Commodity = "SGB",
  RealEstate = "RE",
  ETF = "ETF",
  MutualFund = "MF",
  PMS = "PMS",
  AIF = "AIF",
  StructuredProducts = "SP",
  INVITS = "INVITS",
  Currency = "Currency",
  CDS = "CDS",
  CommodityCo = "CO",
  LIFE_INSURANCE = "LI",
  SAVING_INVESTMENT_INSURANCE = "SII",
  GOVT_SECURITY = "GSC",
  TREASURY_BILLS = "TB",
}
export enum AssetCategory {
  Equity = "EQ",
  FixedIncome = "FI",
  Derivatives = "FnO",
  ManagedPortfolio = "MAP",
  LifeInsurance = "LI",
  RealEstate = "RE",
  Commodity = "CO",
  Others = "Others",
  Total = "Total",
}
export const assetCodesByAssetCategory: ReadonlyMap<string, AssetCategory> =
  Object.freeze(
    new Map([
      ["equity", AssetCategory.Equity],
      ["equity related", AssetCategory.Equity],
      ["fixed income", AssetCategory.FixedIncome],
      ["derivatives", AssetCategory.Derivatives],
      ["managed portfolio", AssetCategory.ManagedPortfolio],
      ["managed portfolios", AssetCategory.ManagedPortfolio],
      ["real estate", AssetCategory.RealEstate],
      ["commodity", AssetCategory.Commodity],
      ["Commodities", AssetCategory.Commodity],
      ["life insurance", AssetCategory.LifeInsurance],
      ["total", AssetCategory.Total],
    ]),
  );
export const NuvamaAssets: ReadonlyMap<
  AssetCategory,
  {
    display: string;
    value: AssetCategory;
  }
> = new Map([
  [
    AssetCategory.Equity,
    {
      display: "Equity",
      value: AssetCategory.Equity,
    },
  ],
  [
    AssetCategory.FixedIncome,
    {
      display: "Fixed Income",
      value: AssetCategory.FixedIncome,
    },
  ],
  [
    AssetCategory.Derivatives,
    {
      display: "Derivative",
      value: AssetCategory.Derivatives,
    },
  ],
  [
    AssetCategory.ManagedPortfolio,
    {
      display: "Managed Portfolio",
      value: AssetCategory.ManagedPortfolio,
    },
  ],
  [
    AssetCategory.LifeInsurance,
    {
      display: "Life Insurance",
      value: AssetCategory.LifeInsurance,
    },
  ],
  [
    AssetCategory.RealEstate,
    {
      display: "Real Estate",
      value: AssetCategory.RealEstate,
    },
  ],
  [
    AssetCategory.Commodity,
    {
      display: "Commodity",
      value: AssetCategory.Commodity,
    },
  ],
]);
export const NuvamaProducts: ReadonlyMap<
  AssetType,
  {
    display: string;
    value: AssetType;
  }
> = new Map([
  [
    AssetType.EQ,
    {
      display: "Listed Equity",
      value: AssetType.EQ,
    },
  ],
  [
    AssetType.UnlistedEquity,
    {
      display: "Unlisted Equity",
      value: AssetType.UnlistedEquity,
    },
  ],
  [
    AssetType.FixedIncome,
    {
      display: "Fixed Income",
      value: AssetType.FixedIncome,
    },
  ],
  [
    AssetType.CertOfDeposit,
    {
      display: "Certificate Of Deposit",
      value: AssetType.CertOfDeposit,
    },
  ],
  [
    AssetType.CommPaper,
    {
      display: "Commercial Paper",
      value: AssetType.CommPaper,
    },
  ],
  [
    AssetType.NCD,
    {
      display: "Non Convertible Debenture",
      value: AssetType.NCD,
    },
  ],
  [
    AssetType.Warrant,
    {
      display: "Warrant",
      value: AssetType.Warrant,
    },
  ],
  [
    AssetType.Bonds,
    {
      display: "Bonds",
      value: AssetType.Bonds,
    },
  ],
  [
    AssetType.Debentures,
    {
      display: "Debentures",
      value: AssetType.Debentures,
    },
  ],
  [
    AssetType.IndianDepoReceipt,
    {
      display: "Indian Depository Receipt",
      value: AssetType.IndianDepoReceipt,
    },
  ],
  [
    AssetType.CorpFD,
    {
      display: "Corp FD",
      value: AssetType.CorpFD,
    },
  ],
  [
    AssetType.RBIBonds,
    {
      display: "RBI Bonds ",
      value: AssetType.RBIBonds,
    },
  ],
  [
    AssetType.EC54,
    {
      display: "54 EC",
      value: AssetType.EC54,
    },
  ],
  [
    AssetType.Derivatives,
    {
      display: "Derivatives",
      value: AssetType.Derivatives,
    },
  ],
  [
    AssetType.DEquity,
    {
      display: "Derivative Equity",
      value: AssetType.DEquity,
    },
  ],
  [
    AssetType.DCurrency,
    {
      display: "Currency",
      value: AssetType.DCurrency,
    },
  ],
  [
    AssetType.DCommodity,
    {
      display: "Commodity",
      value: AssetType.DCommodity,
    },
  ],
  [
    AssetType.PMS,
    {
      display: "PMS",
      value: AssetType.PMS,
    },
  ],
  [
    AssetType.MutualFund,
    {
      display: "Mutual Fund",
      value: AssetType.MutualFund,
    },
  ],
  [
    AssetType.ETF,
    {
      display: "Exchange Traded Funds",
      value: AssetType.ETF,
    },
  ],
  [
    AssetType.INVITS,
    {
      display: "INVITS",
      value: AssetType.INVITS,
    },
  ],
  [
    AssetType.AIF,
    {
      display: "AIF",
      value: AssetType.AIF,
    },
  ],
  [
    AssetType.StructuredProducts,
    {
      display: "Structured Products",
      value: AssetType.StructuredProducts,
    },
  ],
  [
    AssetType.GOVT_SECURITY,
    {
      display: "Govt. Securities",
      value: AssetType.GOVT_SECURITY,
    },
  ],
  [
    AssetType.TREASURY_BILLS,
    {
      display: "Treasury Bills",
      value: AssetType.TREASURY_BILLS,
    },
  ],
  [
    AssetType.MP,
    {
      display: "Managed Portfolios",
      value: AssetType.MP,
    },
  ],
  [
    AssetType.RealEstate,
    {
      display: "Real Estate",
      value: AssetType.RealEstate,
    },
  ],
  [
    AssetType.Commodity,
    {
      display: "Commodities",
      value: AssetType.Commodity,
    },
  ],
]);
export enum EexchangeKeys {
  NSE = "NSE",
  BSE = "BSE",
  NSEF = "NSEF",
  BSEF = "BSEF",
  NSEC = "NSEC",
  BSEC = "BSEC",
  MCX = "MCX",
  NCDEX = "NCDEX",
}
export const assetTypeMappingObj = {
  "Mutual Funds": { assetType: AssetType.MutualFund },
  ETF: { assetType: AssetType.ETF },
  AIF: { assetType: AssetType.AIF },
  PMS: { assetType: AssetType.PMS },
  INVITS: { assetType: AssetType.INVITS },
  "Structured Products": { assetType: AssetType.StructuredProducts },
  "Real Estate": { assetType: AssetType.RealEstate },
  Commodity: { assetType: AssetType.Commodity },
  "Fixed Income": { assetType: AssetType.FixedIncome },
  Derivatives: { assetType: AssetType.Derivatives },
  Equity: { assetType: AssetType.EQ },
};
export const TabMapping = [
  { title: "All", tabEventKey: "pnlAll", assetType: [] },
  {
    title: "Equity",
    tabEventKey: "equity",
    assetType: [AssetType.EQ, AssetType.UnlistedEquity],
  },
  {
    title: "Fixed Income",
    tabEventKey: "fixed-income",
    assetType: [
      AssetType.FixedIncome,
      AssetType.CertOfDeposit,
      AssetType.CommPaper,
      AssetType.NCD,
      AssetType.Warrant,
      AssetType.Bonds,
      AssetType.Debentures,
      AssetType.IndianDepoReceipt,
      AssetType.PreferenceShare,
      AssetType.CorpFD,
      AssetType.RBIBonds,
      AssetType.EC54,
      AssetType.GOVT_SECURITY,
    ],
  },
  {
    title: "Derivatives",
    tabEventKey: "derivative",
    assetType: [
      AssetType.Derivatives,
      AssetType.DEquity,
      AssetType.Currency,
      AssetType.DCommodity,
    ],
  },
  {
    title: "Managed Portfolios",
    tabEventKey: "mp",
    assetType: [
      AssetType.MP,
      AssetType.MutualFund,
      AssetType.ETF,
      AssetType.AIF,
      AssetType.PMS,
      AssetType.INVITS,
      AssetType.StructuredProducts,
    ],
  },
  {
    title: "Others",
    tabEventKey: "others",
    assetType: [AssetType.RealEstate, AssetType.Commodity],
  },
];
export interface IPnLTabData {
  dropDown?: boolean;
  assetType: string;
  startDate: Date;
  endDate: Date;
  finYear: number;
  setStartDate: Function;
  setEndDate: Function;
  setFinYear: Function;
  selectedAssetType?: string;
  fromDate?: string;
  toDate?: string;
  tabName?: string;
  loader?: boolean;
  setLoader?: Function;
  stream: boolean;
  setStream: Function;
  DropDownList: DropDownEntity[];
  setDefaultTab?: Function;
  setAssetTypeOnGoBack?: Function;
  isSummarize: boolean;
  setIsSummarize: Function;
  tillDateSelected: boolean;
  setTillDateSelected: Function;
}
export class PnLReportMainEntityContainer
  implements IPNLReportMainEntityContainer
{
  PnLData: IPNLReportEntity;
  SearchData: string;
  FilterData: FilterData;
  SortData: SortData[];
  IsSummarize: boolean;
  constructor(
    PnLData: IPNLReportEntity,
    SearchData: string,
    FilterData: FilterData,
    SortData: SortData[],
    IsSummarize: boolean,
  ) {
    this.PnLData = PnLData;
    this.SearchData = SearchData;
    this.FilterData = FilterData;
    this.SortData = SortData;
    this.IsSummarize = IsSummarize;
  }
  SummarizeData: boolean;
}

export enum dynamicPNLFilters {
  underlyingAsset = "Underlying Asset",
  expiryMonth = "Expiry Month",
  instrumentType = "Instrument Type",
  optionType = "Option Type",
  strikePrice = "Strike Price",
  simpleFilter = "Filter By",
  assetFilter = "Asset Filter",
  "Filter By" = "Filter By",
  "Asset Filters" = "Asset Filters",
  "Transaction Type" = "Transaction Type Filters",
}
